<template>
  <section class="container py-5" style="max-width:960px">
    <div class="d-flex justify-content-between align-items-center mb-3">
      <h2 class="fw-bold mb-0">Wallet</h2>
      <div class="d-flex align-items-center gap-2">
        <div class="form-check form-switch m-0 d-flex align-items-center gap-2">
          <input class="form-check-input" type="checkbox" id="modeSwitchWallet" :checked="isTestnet" @change="onModeSwitchChange" />
          <label class="form-check-label small" for="modeSwitchWallet">{{ isTestnet ? 'Testnet' : 'Mainnet' }}</label>
        </div>
        <select class="form-select form-select-sm w-auto" v-model.number="selectedChainId" @change="onChainChange">
          <option v-for="c in chains" :key="c.id" :value="c.id">{{ c.label }}</option>
        </select>
        <button v-if="wallet.isConnected" class="btn btn-outline-danger btn-sm" @click="disconnect">Disconnect</button>
        <button v-else class="btn btn-primary btn-sm" @click="connect">Connect</button>
      </div>
    </div>

    <div class="card p-4">
      <div class="row g-3 align-items-center">
        <div class="col-md-8">
          <div class="d-flex flex-wrap align-items-center gap-2">
            <span :class="['badge', chainBadgeClass]">{{ networkName }}</span>
            <code class="small">{{ wallet.address || 'Not connected' }}</code>
            <button v-if="wallet.address" class="btn btn-sm btn-outline-secondary" @click="copyAddress"><i class="bi bi-clipboard me-1"></i>Copy</button>
            <a v-if="wallet.address" class="btn btn-sm btn-outline-secondary" :href="explorerAddress" target="_blank" rel="noopener">
              <i class="bi bi-box-arrow-up-right me-1"></i>{{ explorerLabel }}
            </a>
          </div>
          <div class="text-muted-token small mt-2">Chain ID: {{ chain.current.id }}</div>
        </div>
        <div class="col-md-4 text-md-end">
          <button class="btn btn-outline-secondary btn-sm" @click="openWalletModal"><i class="bi bi-gear me-1"></i>Wallet Settings</button>
        </div>
      </div>
    </div>

    <div class="row g-4 mt-3">
      <div class="col-12 col-lg-7">
        <div class="card p-4 h-100">
          <div class="d-flex justify-content-between align-items-center mb-2">
            <h5 class="mb-0">Tokens</h5>
            <div class="d-flex align-items-center gap-2">
              <input
                type="search"
                class="form-control form-control-sm"
                v-model.trim="tokenQuery"
                placeholder="Search tokens..."
                style="max-width:220px"
              />
              <button class="btn btn-sm btn-outline-secondary" @click="onTokensRefresh" :title="'Refresh detected tokens & balances'"><i class="bi bi-arrow-repeat"></i></button>
            </div>
          </div>
          <div class="small text-muted-token" v-if="tokensMeta && tokensMeta.updatedAt">
            <template v-if="tokensMeta.cached">Showing cached results</template>
            <template v-else>Just refreshed</template>
            • Updated {{ rel(tokensMeta.updatedAt) }}
            <template v-if="tokensMeta.cooldownSecondsRemaining && tokensMeta.cooldownSecondsRemaining > 0 && tokensMeta.nextRefreshAt">
              • You can refresh again {{ rel(tokensMeta.nextRefreshAt) }}
            </template>
          </div>
          <div v-if="loadingTokens" class="text-muted-token small">Loading tokens…</div>
          <div v-else-if="tokensError" class="text-danger small">{{ tokensError }}</div>
          <div v-else>
            <div v-if="filteredTokenItems.length === 0" class="text-muted-token small">
              <template v-if="tokenQuery">No tokens match your search.</template>
              <template v-else>No tokens found on this network.</template>
            </div>
            <ul class="list-unstyled mb-0">
              <li v-for="t in filteredTokenItems" :key="t.address" class="d-flex justify-content-between align-items-center py-2 border-bottom">
                <div class="d-flex align-items-center gap-2">
                  <img v-if="t.logoURI" :src="t.logoURI" :alt="t.symbol" width="20" height="20" style="border-radius:4px" />
                  <div>
                    <div class="fw-semibold">{{ t.symbol }}</div>
                    <div class="small text-muted-token">{{ t.name }}</div>
                  </div>
                </div>
                <div class="text-end">
                  <div class="fw-semibold">{{ t.balance }}</div>
                  <div class="small"><a :href="tokenLink(t.address)" target="_blank" rel="noopener">View</a></div>
                </div>
              </li>
            </ul>
          </div>
        </div>
      </div>
      <div class="col-12 col-lg-5">
        <div class="card p-4 h-100">
          <div class="d-flex justify-content-between align-items-center mb-2">
            <h5 class="mb-0">NFTs</h5>
            <button class="btn btn-sm btn-outline-secondary" @click="onNftsRefresh"><i class="bi bi-arrow-repeat"></i></button>
          </div>
          <div class="small text-muted-token" v-if="nftsMeta && nftsMeta.updatedAt">
            <template v-if="nftsMeta.cached">Showing cached results</template>
            <template v-else>Just refreshed</template>
            • Updated {{ rel(nftsMeta.updatedAt) }}
            <template v-if="nftsMeta.cooldownSecondsRemaining && nftsMeta.cooldownSecondsRemaining > 0 && nftsMeta.nextRefreshAt">
              • You can refresh again {{ rel(nftsMeta.nextRefreshAt) }}
            </template>
          </div>
          <div v-if="loadingNfts" class="text-muted-token small">Loading NFTs…</div>
          <div v-else-if="nftError" class="text-danger small">{{ nftError }}</div>
          <div v-else>
            <div v-if="nfts.length === 0" class="text-muted-token small">No NFTs found on this network.</div>
            <div class="row g-3" v-else>
              <div class="col-6" v-for="n in nfts" :key="n.contractAddress + ':' + n.tokenId">
                <div class="border rounded p-2 h-100 d-flex flex-column">
                  <div class="ratio ratio-1x1 mb-2 bg-body-tertiary d-flex align-items-center justify-content-center overflow-hidden" style="border-radius:8px">
                    <img v-if="n.image" :src="n.image" :alt="n.name || 'NFT'" class="w-100 h-100 object-fit-cover" />
                    <i v-else class="bi bi-image text-muted" style="font-size:24px"></i>
                  </div>
                  <div class="small fw-semibold text-truncate">{{ n.name || 'NFT' }}</div>
                  <div class="small text-muted-token">#{{ n.tokenId }}</div>
                  <div class="mt-auto pt-1">
                    <a :href="nftLink(n.contractAddress, n.tokenId)" target="_blank" rel="noopener" class="small">View</a>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </section>
</template>

<script setup lang="ts">
import { computed, onMounted, onBeforeUnmount, ref, watch } from 'vue';
import { useWalletStore } from '../stores/wallet';
import { useChainStore } from '../stores/chain';
import { useTokens } from '../composables/tokens';
import { useNfts } from '../composables/nfts';
import { formatRelativeTime } from '../utils/time';

const wallet = useWalletStore();
wallet.init();
const chain = useChainStore();
chain.init();

const chains = computed(() => chain.supported);
const selectedChainId = ref<number>(chain.selectedId);
const TESTNET_ID = 11155111;
const DEFAULT_MAINNET_ID = Number((process.env.VUE_APP_DEFAULT_CHAIN_ID as string) || '1');
const isTestnet = computed(() => chain.selectedId === TESTNET_ID);

async function setMode(toTestnet: boolean) {
  const targetId = toTestnet ? TESTNET_ID : DEFAULT_MAINNET_ID;
  selectedChainId.value = targetId;
  await onChainChange();
}
async function onModeSwitchChange(ev: Event) {
  const target = ev.target as HTMLInputElement;
  await setMode(!!target.checked);
}
async function onChainChange() {
  chain.select(selectedChainId.value);
  try { await wallet.ensureChain(chain.current.chain); } catch {}
}

const { tokens, balances, loading: loadingRef, error: errorRef, load, refreshBalances, serverMeta, refreshServerTokens } = useTokens();
const { items: nftItems, loading: loadingNftsRef, error: nftErrorRef, load: loadNfts, meta: nftsMetaRef } = useNfts();
const loadingTokens = computed(() => loadingRef.value);
const tokensError = computed(() => errorRef.value);
const tokenItems = computed(() => (tokens.value || []).map(t => ({
  ...t,
  balance: balances.value[t.address] || '0'
})));
const tokenQuery = ref('');
const filteredTokenItems = computed(() => {
  const q = tokenQuery.value.trim().toLowerCase();
  if (!q) return tokenItems.value;
  return tokenItems.value.filter((t: any) => {
    return (
      (t.symbol && String(t.symbol).toLowerCase().includes(q)) ||
      (t.name && String(t.name).toLowerCase().includes(q)) ||
      (t.address && String(t.address).toLowerCase().includes(q))
    );
  });
});
const nfts = computed(() => nftItems.value || []);
const loadingNfts = computed(() => loadingNftsRef.value);
const nftError = computed(() => nftErrorRef.value);
const tokensMeta = computed(() => serverMeta.value || null);
const nftsMeta = computed(() => nftsMetaRef.value || null);

onMounted(async () => {
  await load();
  await refreshBalances();
  await loadNfts();
  startPolling();
});

watch(() => wallet.address, async () => {
  await load();
  await refreshBalances();
  await loadNfts();
});
watch(() => chain.selectedId, async () => {
  selectedChainId.value = chain.selectedId;
  await load();
  await refreshBalances();
  await loadNfts();
});

async function onTokensRefresh() {
  await refreshServerTokens();
  await refreshBalances();
}

async function onNftsRefresh() {
  await loadNfts(true, true);
}

const networkName = computed(() => chain.current.label);
const chainBadgeClass = computed(() => chain.current.id === TESTNET_ID ? 'badge-tinted-accent' : 'badge-tinted-primary');
const explorerBase = computed(() => chain.current.explorerBase || 'https://sepolia.etherscan.io');
const explorerAddress = computed(() => wallet.address ? `${explorerBase.value}/address/${wallet.address}` : '#');
const explorerLabel = computed(() => explorerBase.value.includes('etherscan') ? 'View on Etherscan' : 'View on Explorer');

function tokenLink(addr: string) {
  // Prefer token holdings page on *scan explorers; fallback to token page
  const base = explorerBase.value;
  if (/scan\.io|etherscan|arbiscan|polygonscan|basescan/.test(base)) {
    return wallet.address ? `${base}/token/${addr}?a=${wallet.address}` : `${base}/token/${addr}`;
  }
  return `${base}/token/${addr}`;
}

const nftTransfersUrl = computed(() => wallet.address ? `${explorerBase.value}/address/${wallet.address}#nfttransfers` : '#');
function nftLink(contract: string, tokenId: string) {
  return `${explorerBase.value}/token/${contract}?a=${wallet.address || ''}&tokenId=${tokenId}`;
}

function rel(ts?: string | null) {
  return formatRelativeTime(ts || undefined);
}

async function copyAddress() {
  if (!wallet.address) return;
  try { await navigator.clipboard.writeText(wallet.address); } catch {}
}

async function disconnect() {
  await wallet.disconnect();
}

async function connect() {
  try {
    const mod = await import('@reown/appkit/vue-core');
    const { open } = mod.useAppKit();
    await open();
  } catch {
    // fallback to hidden element open
    const btn = document.createElement('w3m-button');
    btn.style.display = 'none';
    document.body.appendChild(btn);
    (btn as any).open?.();
    setTimeout(() => btn.remove(), 2000);
  }
}

async function openWalletModal() {
  // Try to open AppKit modal if available
  try {
    const mod = await import('@reown/appkit/vue-core');
    const { open } = mod.useAppKit();
    await open();
  } catch {}
}

// Polling
let pollTimer: any = null;
function startPolling() {
  stopPolling();
  pollTimer = setInterval(async () => {
    await load();
    await refreshBalances();
    await loadNfts();
  }, 30000);
}
function stopPolling() {
  if (pollTimer) { clearInterval(pollTimer); pollTimer = null; }
}
onBeforeUnmount(stopPolling);
</script>

<style scoped>
.card { border-radius: var(--radius); }
</style>
